/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import * as actionType from './actionTypes';

const initialState = {
  filterParams: {
    range: '',
    interval: '',
    start: '',
    end: '',
    intents: [],
    entities: [],
    notContained: false,
    notCovered: false,
    search: [],
    noIntents: false,
    noEntities: false,
    language: '',
    workspace: '',
    skillReference: ''
  },
  improveStateParams: {},
  improvePreserveParams: {},
  improveSource: {},
  skillHasData: {
    loading: false,
    results: {
      hasData: true,
      hasUncontainedData: true,
      hasUncoveredData: true
    },
    error: null
  },
  autolearnMetrics: {
    reports: null,
    loading: false,
    error: null
  },
  autolearnSamples: {
    reports: null,
    loading: false,
    error: null
  },
  userAndSession: {
    loading: false,
    reports: null,
    error: null
  },
  weakUnderstanding: {
    loading: false,
    reports: null,
    error: null
  },
  topIntents: {
    loading: false,
    reports: null,
    error: null
  },
  topEntities: {
    loading: false,
    reports: null,
    error: null
  },
  reports: {
    loading: false,
    results: null,
    error: null
  },
  deploymentList: {
    loading: false,
    results: null,
    error: null
  },
  conversationLogs: {
    resultsFound: false,
    resultCount: 0,
    messages: [],
    nextLogsSet: '',
    loading: false,
    error: null,
    currentIndex: 0,
    noLogs: false,
    reachedPageLimit: false,
    pageIndex: 0,
    hasFilterResults: false
  },
  currentConversation: {
    currentConversation: {},
    error: null,
    selectedUtterance: {}
  },
  editLogs: {
    loading: false,
    error: null
  },
  toggleConversation: false,
  containment: {
    loading: false,
    reports: {},
    error: null
  },
  coverage: {
    loading: false,
    reports: {},
    error: null
  }
};

const fetchSkillHasDataStart = state => ({
  ...state,
  skillHasData: {
    loading: true,
    results: { ...state.skillHasData.results },
    error: null
  }
});
const fetchSkillHasDataSuccess = (state, action) => ({
  ...state,
  skillHasData: { loading: false, results: action.results, error: null }
});
const fetchSkillHasDataError = (state, action) => ({
  ...state,
  skillHasData: {
    loading: false,
    results: {
      hasData: true,
      hasUncontainedData: true,
      hasUncoveredData: true
    },
    error: action.error
  }
});

const fetchAutolearnMetricsStart = state => ({
  ...state,
  autolearnMetrics: { loading: true, error: null }
});
const fetchAutolearnMetricsSuccess = (state, action) => ({
  ...state,
  autolearnMetrics: { loading: false, reports: action.reports, error: null }
});
const fetchAutolearnMetricsError = (state, action) => ({
  ...state,
  autolearnMetrics: { loading: false, reports: null, error: action.error }
});

const fetchAutolearnSamplesStart = state => ({
  ...state,
  autolearnSamples: { loading: true, error: null }
});
const fetchAutolearnSamplesSuccess = (state, action) => ({
  ...state,
  autolearnSamples: { loading: false, reports: action.reports, error: null }
});
const fetchAutolearnSamplesError = (state, action) => ({
  ...state,
  autolearnSamples: { loading: false, samples: null, error: action.error }
});

const fetchUserAndSessionStart = state => ({
  ...state,
  userAndSession: { loading: true, error: null }
});
const fetchUserAndSessionSuccess = (state, action) => ({
  ...state,
  userAndSession: { loading: false, reports: action.reports, error: null }
});
const fetchUserAndSessionError = (state, action) => ({
  ...state,
  userAndSession: { loading: false, reports: null, error: action.error }
});

const fetchWeakUnderstandingStart = state => ({
  ...state,
  weakUnderstanding: { loading: true, error: null }
});
const fetchWeakUnderstandingSuccess = (state, action) => ({
  ...state,
  weakUnderstanding: { loading: false, reports: action.reports, error: null }
});
const fetchWeakUnderstandingError = (state, action) => ({
  ...state,
  weakUnderstanding: { loading: false, reports: null, error: action.error }
});

const fetchTopIntentsStart = state => ({
  ...state,
  topIntents: { loading: true, error: null }
});
const fetchTopIntentsSuccess = (state, action) => ({
  ...state,
  topIntents: { loading: false, reports: action.reports, error: null }
});
const fetchTopIntentsError = (state, action) => ({
  ...state,
  topIntents: { loading: false, reports: null, error: action.error }
});

const fetchTopEntitiesStart = state => ({
  ...state,
  topEntities: { loading: true, error: null }
});
const fetchTopEntitiesSuccess = (state, action) => ({
  ...state,
  topEntities: { loading: false, reports: action.reports, error: null }
});
const fetchTopEntitiesError = (state, action) => ({
  ...state,
  topEntities: { loading: false, reports: null, error: action.error }
});

const fetchSkillReferenceStart = state => ({
  ...state,
  reports: { loading: true, error: null }
});
const fetchSkillReferenceSuccess = (state, action) => ({
  ...state,
  reports: { loading: false, results: action.reports, error: null }
});
const fetchSkillReferenceError = (state, action) => ({
  ...state,
  reports: { loading: false, results: null, error: action.error }
});

const fetchDeploymentListStart = state => ({
  ...state,
  deploymentList: { loading: true, error: null }
});
const fetchDeploymentListSuccess = (state, action) => ({
  ...state,
  deploymentList: { loading: false, results: action.reports, error: null }
});
const fetchDeploymentListError = (state, action) => ({
  ...state,
  deploymentList: { loading: false, results: null, error: action.error }
});

const fetchContainmentStart = state => ({
  ...state,
  containment: { loading: true, reports: {}, error: null }
});
const fetchContainmentSuccess = (state, action) => ({
  ...state,
  containment: {
    loading: false,
    reports: action.reports,
    error: null
  }
});
const fetchContainmentError = (state, action) => ({
  ...state,
  containment: {
    loading: false,
    reports: {},
    error: action.error
  }
});

const fetchCoverageStart = state => ({
  ...state,
  coverage: { loading: true, reports: {}, error: null }
});
const fetchCoverageSuccess = (state, action) => ({
  ...state,
  coverage: {
    loading: false,
    reports: action.reports,
    error: null
  }
});
const fetchCoverageError = (state, action) => ({
  ...state,
  coverage: {
    loading: false,
    reports: {},
    error: action.error
  }
});

const setFilterParams = (state, action) => ({
  ...state,
  filterParams: {
    ...action.filterParams
  }
});

const setImproveStateParams = (state, action) => ({
  ...state,
  improveStateParams: {
    ...action.stateUpdate
  }
});

const setImprovePreserveParams = (state, action) => ({
  ...state,
  improvePreserveParams: {
    ...action.preserveParams
  }
});

const setImproveSource = (state, action) => ({
  ...state,
  improveSource: {
    ...action.source
  }
});

const fetchConversationLogsStart = state => ({
  ...state,
  conversationLogs: { ...state.conversationLogs, loading: true, error: null }
});
const fetchConversationLogsSuccess = (state, action, allLogs) => ({
  ...state,
  conversationLogs: {
    loading: false,
    resultsFound: action.resultsFound,
    resultCount: action.resultCount,
    messages: allLogs,
    nextLogsSet: action.nextLogsSet,
    error: null,
    currentIndex: action.currentIndex,
    noLogs: action.noLogs,
    reachedPageLimit: action.reachedPageLimit,
    pageIndex: action.pageIndex,
    hasFilterResults: action.hasFilterResults
  }
});
const fetchConversationLogsError = (state, action) => ({
  ...state,
  conversationLogs: {
    ...state.conversationLogs,
    loading: false,
    error: action.error
  }
});

const fetchSingleConversationStart = state => ({ ...state });
const fetchSingleConversationSuccess = (state, action) => ({
  ...state,
  currentConversation: {
    currentConversation: action.currentConversation,
    selectedUtterance: action.selectedUtterance
  }
});
const fetchSingleConversationError = (state, action) => ({
  ...state,
  currentConversation: { error: action.error }
});

const saveLogEditsStart = state => ({
  ...state,
  editLogs: { loading: true, error: null }
});
const saveLogEditsSuccess = state => ({
  ...state,
  editLogs: { loading: false, error: null }
});
const saveLogEditsError = (state, action) => ({
  ...state,
  editLogs: { loading: false, error: action.error }
});

const clearImproveStateParams = state => ({
  ...state,
  improveStateParams: initialState.improveStateParams
});

const clearData = state => ({
  ...state,
  filterParams: initialState.filterParams,
  conversationLogs: initialState.conversationLogs
});

const addLogs = (oldArray, newArray, gotMoreLogs) => {
  if (!gotMoreLogs) {
    return newArray;
  } else if (oldArray.length % 250 === 0) {
    return newArray;
  }
  return oldArray.concat(newArray);
};

const toggleConversations = (state, action) => ({
  ...state,
  toggleConversation: action.value
});

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case actionType.SET_FILTER_PARAMS:
      return setFilterParams(state, action);
    case actionType.SET_IMPROVE_STATE:
      return setImproveStateParams(state, action);
    case actionType.SET_IMPROVE_PRSRV:
      return setImprovePreserveParams(state, action);
    case actionType.SET_IMPROVE_SOURCE:
      return setImproveSource(state, action);
    case actionType.FETCH_SKILL_HAS_DATA_START:
      return fetchSkillHasDataStart(state, action);
    case actionType.FETCH_SKILL_HAS_DATA_SUCCESS:
      return fetchSkillHasDataSuccess(state, action);
    case actionType.FETCH_SKILL_HAS_DATA_ERROR:
      return fetchSkillHasDataError(state, action);
    case actionType.FETCH_AUTOLEARN_METRICS_START:
      return fetchAutolearnMetricsStart(state, action);
    case actionType.FETCH_AUTOLEARN_METRICS_SUCCESS:
      return fetchAutolearnMetricsSuccess(state, action);
    case actionType.FETCH_AUTOLEARN_METRICS_ERROR:
      return fetchAutolearnMetricsError(state, action);
    case actionType.FETCH_AUTOLEARN_SAMPLES_START:
      return fetchAutolearnSamplesStart(state, action);
    case actionType.FETCH_AUTOLEARN_SAMPLES_SUCCESS:
      return fetchAutolearnSamplesSuccess(state, action);
    case actionType.FETCH_AUTOLEARN_SAMPLES_ERROR:
      return fetchAutolearnSamplesError(state, action);
    case actionType.FETCH_USER_AND_SESSION_START:
      return fetchUserAndSessionStart(state, action);
    case actionType.FETCH_USER_AND_SESSION_SUCCESS:
      return fetchUserAndSessionSuccess(state, action);
    case actionType.FETCH_USER_AND_SESSION_ERROR:
      return fetchUserAndSessionError(state, action);
    case actionType.FETCH_WEAK_UNDERSTANDING_START:
      return fetchWeakUnderstandingStart(state, action);
    case actionType.FETCH_WEAK_UNDERSTANDING_SUCCESS:
      return fetchWeakUnderstandingSuccess(state, action);
    case actionType.FETCH_WEAK_UNDERSTANDING_ERROR:
      return fetchWeakUnderstandingError(state, action);
    case actionType.FETCH_TOP_INTENTS_START:
      return fetchTopIntentsStart(state, action);
    case actionType.FETCH_TOP_INTENTS_SUCCESS:
      return fetchTopIntentsSuccess(state, action);
    case actionType.FETCH_TOP_INTENTS_ERROR:
      return fetchTopIntentsError(state, action);
    case actionType.FETCH_TOP_ENTITIES_START:
      return fetchTopEntitiesStart(state, action);
    case actionType.FETCH_TOP_ENTITIES_SUCCESS:
      return fetchTopEntitiesSuccess(state, action);
    case actionType.FETCH_TOP_ENTITIES_ERROR:
      return fetchTopEntitiesError(state, action);
    case actionType.FETCH_SKILL_REFERENCE_START:
      return fetchSkillReferenceStart(state, action);
    case actionType.FETCH_SKILL_REFERENCE_SUCCESS:
      return fetchSkillReferenceSuccess(state, action);
    case actionType.FETCH_SKILL_REFERENCE_ERROR:
      return fetchSkillReferenceError(state, action);
    case actionType.FETCH_DEPLOYMENT_LIST_START:
      return fetchDeploymentListStart(state, action);
    case actionType.FETCH_DEPLOYMENT_LIST_SUCCESS:
      return fetchDeploymentListSuccess(state, action);
    case actionType.FETCH_DEPLOYMENT_LIST_ERROR:
      return fetchDeploymentListError(state, action);
    case actionType.FETCH_CONTAINMENT_START:
      return fetchContainmentStart(state, action);
    case actionType.FETCH_CONTAINMENT_SUCCESS:
      return fetchContainmentSuccess(state, action);
    case actionType.FETCH_CONTAINMENT_ERROR:
      return fetchContainmentError(state, action);
    case actionType.FETCH_COVERAGE_START:
      return fetchCoverageStart(state, action);
    case actionType.FETCH_COVERAGE_SUCCESS:
      return fetchCoverageSuccess(state, action);
    case actionType.FETCH_COVERAGE_ERROR:
      return fetchCoverageError(state, action);
    case actionType.FETCH_CONVERSATION_LOGS_START:
      return fetchConversationLogsStart(state, action);
    case actionType.FETCH_CONVERSATION_LOGS_SUCCESS: {
      const allLogs = addLogs(
        state.conversationLogs.messages,
        action.logs,
        action.gotMoreLogs
      );
      return fetchConversationLogsSuccess(state, action, allLogs);
    }
    case actionType.FETCH_CONVERSATION_LOGS_FAILURE:
      return fetchConversationLogsError(state, action);
    case actionType.FETCH_SINGLE_CONVERSATION_START:
      return fetchSingleConversationStart(state, action);
    case actionType.FETCH_SINGLE_CONVERSATION_SUCCESS: {
      return fetchSingleConversationSuccess(state, action);
    }
    case actionType.FETCH_SINGLE_CONVERSATION_FAILURE:
      return fetchSingleConversationError(state, action);
    case actionType.EDIT_CONVERSATION_LOGS_START:
      return saveLogEditsStart(state, action);
    case actionType.EDIT_CONVERSATION_LOGS_SUCCESS:
      return saveLogEditsSuccess(state, action);
    case actionType.EDIT_CONVERSATION_LOGS_FAILURE:
      return saveLogEditsError(state, action);
    case actionType.CLEAR_DATA:
      return clearData(state, action);
    case actionType.CLEAR_IMPROVE_STATE_PARAMS:
      return clearImproveStateParams(state, action);
    case actionType.TOGGLE_CONVERSATIONS:
      return toggleConversations(state, action);
    default:
      return state;
  }
};

export default reducer;
